/**
 * Zgodovina odigranih skladb in agregiran čas poslušanja (T2 §2).
 *
 * Domenski sloj: čiste funkcije brez shrambe, DOM in ure. Zgodovina sama
 * živi v IndexedDB (T2 §3, neomejeno raste), stanje `ListenStat` pa v
 * localStorage prek `StoragePort` (ključ `stats`). Tu je le logika nad njima:
 * združevanje, filtriranje in seštevanje časa.
 */

/** Ena zabeležena skladba (T2 §2). */
export interface HistoryEntry {
  id: string;
  stationId: string;
  artist?: string;
  title: string;
  /**
   * Izvirni ICY niz, pred razčlenjevanjem.
   *
   * Hrani se poleg razčlenjenega: če razčlenjevalnik kdaj popravimo, se da
   * zgodovino znova obdelati (T2 §2). Brez tega je popravek za nazaj nemogoč.
   */
  raw: string;
  at: number;
  artworkUrl?: string;
}

/** Čas poslušanja, agregiran po postaji (T2 §2). */
export interface ListenStat {
  stationId: string;
  totalMs: number;
  /** Koliko ločenih odsekov poslušanja — vsak zagon predvajanja je eden. */
  sessions: number;
  lastAt: number;
}

/**
 * Skladbe ene postaje, v obratnem časovnem redu (najnovejša zgoraj).
 *
 * Vrstni red skupin sledi **najnovejšemu vnosu skupine**: postaja, ki si jo
 * pravkar poslušal, je na vrhu. Znotraj skupine so vnosi od najnovejšega.
 */
export interface HistoryGroup {
  stationId: string;
  entries: HistoryEntry[];
}

/**
 * Združi vnose po postaji, red skupin po zadnjem poslušanju.
 *
 * Predpostavlja, da so `entries` že v obratnem časovnem redu (tako jih vrne
 * shramba). Prvo pojavljanje postaje določi mesto skupine; s tem je postaja z
 * najnovejšo skladbo prva, brez ločenega razvrščanja.
 */
export function groupHistory(entries: readonly HistoryEntry[]): HistoryGroup[] {
  const groups: HistoryGroup[] = [];
  const byId = new Map<string, HistoryGroup>();

  for (const entry of entries) {
    let group = byId.get(entry.stationId);
    if (group === undefined) {
      group = { stationId: entry.stationId, entries: [] };
      byId.set(entry.stationId, group);
      groups.push(group);
    }
    group.entries.push(entry);
  }

  return groups;
}

/**
 * Filtriraj po iskalnem nizu (P2 §4.3).
 *
 * Prazen niz vrne vse. Ujema se po izvajalcu in naslovu, neobčutljivo na
 * velike črke in obrobne presledke — ne po `raw`, ker ta nosi krmilne znake
 * in podvojenega izvajalca, ki bi zadetke tiho popačila.
 */
export function filterHistory(entries: readonly HistoryEntry[], query: string): HistoryEntry[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...entries];

  return entries.filter((entry) => {
    const haystack = `${entry.artist ?? ''} ${entry.title}`.toLowerCase();
    return haystack.includes(needle);
  });
}

/** Statistika ene postaje; `null`, kadar je še ni. */
export function statFor(stats: readonly ListenStat[], stationId: string): ListenStat | null {
  return stats.find((s) => s.stationId === stationId) ?? null;
}

/**
 * Začetek odseka poslušanja: zabeleži nov odsek in čas.
 *
 * Kliče se ob prehodu postaje v predvajanje. `totalMs` se tu ne dotakne —
 * čas prišteje `addListenTime` ob koncu odseka, ko je znan (T2, brez ugibanja
 * trajanja vnaprej).
 */
export function startListenSession(
  stats: readonly ListenStat[],
  stationId: string,
  now: number,
): ListenStat[] {
  const existing = statFor(stats, stationId);
  if (existing === null) {
    return [...stats, { stationId, totalMs: 0, sessions: 1, lastAt: now }];
  }
  return stats.map((s) =>
    s.stationId === stationId ? { ...s, sessions: s.sessions + 1, lastAt: now } : s,
  );
}

/**
 * Prištej pretečeni čas odseka postaji.
 *
 * `deltaMs <= 0` ne naredi ničesar: negativna razlika bi nastala le ob
 * premiku ure nazaj in seštevanje takega »časa« ni podatek. Zapis mora že
 * obstajati (`startListenSession` ga ustvari ob začetku odseka).
 */
export function addListenTime(
  stats: readonly ListenStat[],
  stationId: string,
  deltaMs: number,
  now: number,
): ListenStat[] {
  if (deltaMs <= 0) return [...stats];
  return stats.map((s) =>
    s.stationId === stationId ? { ...s, totalMs: s.totalMs + deltaMs, lastAt: now } : s,
  );
}

/** Odstrani zgodovino in čas ene postaje (P2 §4.7). */
export function dropStationStats(stats: readonly ListenStat[], stationId: string): ListenStat[] {
  return stats.filter((s) => s.stationId !== stationId);
}

/* ── Obrambno branje ───────────────────────────────────────────────────
   Kot pri nastavitvah (settings.ts): shramba je lahko pokvarjena z ročnim
   urejanjem, prekinjenim zapisom ali uvozom tuje datoteke. Branje ne sme
   predpostaviti oblike — migracija sme, branje ne. */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseHistory(raw: unknown): HistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  const entries: HistoryEntry[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    if (typeof item.id !== 'string') continue;
    if (typeof item.stationId !== 'string') continue;
    if (typeof item.title !== 'string' || item.title === '') continue;
    if (typeof item.at !== 'number') continue;

    const entry: HistoryEntry = {
      id: item.id,
      stationId: item.stationId,
      title: item.title,
      raw: typeof item.raw === 'string' ? item.raw : item.title,
      at: item.at,
    };
    if (typeof item.artist === 'string' && item.artist !== '') entry.artist = item.artist;
    if (typeof item.artworkUrl === 'string' && item.artworkUrl !== '') {
      entry.artworkUrl = item.artworkUrl;
    }
    entries.push(entry);
  }
  return entries;
}

export function parseStats(raw: unknown): ListenStat[] {
  if (!Array.isArray(raw)) return [];
  const stats: ListenStat[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    if (typeof item.stationId !== 'string') continue;
    const totalMs = typeof item.totalMs === 'number' && item.totalMs >= 0 ? item.totalMs : 0;
    const sessions = typeof item.sessions === 'number' && item.sessions >= 0 ? item.sessions : 0;
    const lastAt = typeof item.lastAt === 'number' ? item.lastAt : 0;
    stats.push({ stationId: item.stationId, totalMs, sessions, lastAt });
  }
  return stats;
}
